import { useEffect, useMemo, useState } from "react";
import { IconSearch } from "@tabler/icons-react";
import { Card, Input, PaginadorTabla, Stack } from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import type { EquivalenciaUnidad } from "@/lib/types";
import { listarEquivalenciasPaginado } from "../services/equivalencias-service";
import { useEquivalenciasAdmin } from "../hooks/use-equivalencias-admin";
import { EquivalenciasHeader } from "./EquivalenciasHeader";
import { EquivalenciaCrearForm } from "./EquivalenciaCrearForm";
import { EquivalenciasTable } from "./EquivalenciasTable";

const PAGE_SIZE = 15;

/**
 * Página de administración del catálogo de equivalencias de unidad SAT↔SAP.
 * Container thin · orquesta `modoCrear` y la búsqueda + paginado SERVER-SIDE
 * (query params con debounce · primera página precargada por el loader).
 */
export function AdminEquivalenciasPage() {
  const [busqueda, setBusqueda] = useState("");
  const [page, setPage] = useState(1);
  const [busquedaDebounced] = useDebouncedValue(busqueda, 350);
  const [modoCrear, setModoCrear] = useState(false);

  const serverParams = useMemo(
    () => ({
      ...(busquedaDebounced.trim() ? { busqueda: busquedaDebounced.trim() } : {}),
      page,
      pageSize: PAGE_SIZE,
    }),
    [busquedaDebounced, page]
  );

  useEffect(() => {
    setPage(1);
  }, [busquedaDebounced]);

  const { data } = useEquivalenciasAdmin(serverParams);
  const equivalencias = data?.items ?? [];
  const total = data?.total ?? 0;

  const obtenerTodas = async (): Promise<EquivalenciaUnidad[]> => {
    const b = busquedaDebounced.trim();
    const todas: EquivalenciaUnidad[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarEquivalenciasPaginado({ ...(b ? { busqueda: b } : {}), page: p, pageSize: 100 });
      todas.push(...pagina.items);
      if (todas.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todas;
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <EquivalenciasHeader total={total} modoCrear={modoCrear} onIniciarCrear={() => setModoCrear(true)} />
      <Stack gap="md">
        {modoCrear ? <EquivalenciaCrearForm onSalir={() => setModoCrear(false)} /> : null}
        <Input
          placeholder="Buscar por clave SAT, unidad SAP o descripción…"
          leftSection={<IconSearch size={14} stroke={1.7} />}
          value={busqueda}
          onChange={(e) => setBusqueda(e.currentTarget.value)}
          wrapperClassName="max-w-90"
        />
        <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} variante="superior" />
        <Card padding="lg">
          <EquivalenciasTable equivalencias={equivalencias} obtenerFilasExport={obtenerTodas} />
        </Card>
        <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="equivalencias" />
      </Stack>
    </div>
  );
}
